import moment from "moment";
import { useEffect, useState } from "react";

import Button from "@components/button/button";
import { ButtonSize } from "@components/button/button-constants";

import TypingText from "./typing-text";

const retryMessageTimeout = 15 * 1000;

const AskAILoading = ({
  initialLabel,
  onClickRetry,
  lastUpdate,
  showStaleRetryCTA = true,
}: {
  initialLabel: string;
  onClickRetry: () => void;
  lastUpdate?: string | null;
  showStaleRetryCTA?: boolean;
}) => {
  const [justAMoment, setJustAMoment] = useState(false);
  const [takingLongerThanExpected, setTakingLongerThanExpected] =
    useState(false);
  const [stillWorkingOnIt, setStillWorkingOnIt] = useState(false);
  const [somethingHasGoneWrong, setSomethingHasGoneWrong] = useState(false);
  const [showRetryCTA, setShowRetryCTA] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setJustAMoment(true);
    }, retryMessageTimeout);
    return () => clearTimeout(timer);
  }, [initialLabel]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setTakingLongerThanExpected(true);
    }, retryMessageTimeout * 2);
    return () => clearTimeout(timer);
  }, [initialLabel]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setStillWorkingOnIt(true);
    }, retryMessageTimeout * 3);
    return () => clearTimeout(timer);
  }, [initialLabel]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSomethingHasGoneWrong(true);
    }, retryMessageTimeout * 4);
    return () => clearTimeout(timer);
  }, [initialLabel]);

  const handleCompleted = () => {
    if (somethingHasGoneWrong) {
      setShowRetryCTA(true);
    }
  };

  const fewMinutesAgo = moment().subtract(3, "minute");
  if (
    showStaleRetryCTA &&
    lastUpdate &&
    moment(lastUpdate).isBefore(fewMinutesAgo)
  ) {
    return (
      <div className="flex items-center gap-2">
        No answers yet.
        <Button
          size={ButtonSize.medium}
          text="Try again?"
          onClick={onClickRetry}
        />
      </div>
    );
  }

  return (
    <div className="text-gray-500 mb-10 line-height-[20px]">
      {!justAMoment && (
        <TypingText
          key={`thinking-${initialLabel}`}
          className="mr-1"
          text={initialLabel}
        />
      )}
      {justAMoment && (
        <TypingText
          key="just-a-moment"
          className="mr-1"
          text="Just a moment..."
        />
      )}
      {takingLongerThanExpected && (
        <TypingText
          key="taking-longer-than-expected"
          text="This is taking longer than expected..."
          className="mr-1"
        />
      )}
      {stillWorkingOnIt && (
        <TypingText
          key="still-working-on-it"
          text="Still working on it... Thank you for your patience."
          className="mr-1"
        />
      )}
      {somethingHasGoneWrong && (
        <TypingText
          key="something-might-have-gone-wrong"
          text="Something might have gone wrong. Refresh the page or"
          onCompleted={handleCompleted}
          className="mr-1"
        />
      )}
      {showRetryCTA ? (
        <Button
          size={ButtonSize.medium}
          text="Try again?"
          onClick={onClickRetry}
          className="mr-1"
        />
      ) : (
        <span className="h-5 align-bottom inline-flex items-center mr-1">
          <div className="h-4 w-2 bg-black animate-blink" />
        </span>
      )}
    </div>
  );
};

export default AskAILoading;
